import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { AlertCircle } from 'lucide-react';
import * as authApi from '@/api/auth.api';
import { ApiError } from '@/api/client';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Modal } from '@/components/ui/Modal';
import { useToast } from '@/hooks/useToast';

/**
 * "Cambiar contraseña" del usuario con sesión: actual + nueva + confirmación.
 * Misma regla que el restablecimiento (mínimo 8). Al guardar, las otras
 * sesiones del usuario se cierran; esta sigue abierta.
 */
export function CambiarClaveModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const toast = useToast();
  const [actual, setActual] = useState('');
  const [nueva, setNueva] = useState('');
  const [confirmar, setConfirmar] = useState('');
  const [error, setError] = useState<string | null>(null);

  function cerrar() {
    setActual('');
    setNueva('');
    setConfirmar('');
    setError(null);
    onClose();
  }

  const cambio = useMutation({
    mutationFn: () => authApi.changePassword({ currentPassword: actual, newPassword: nueva }),
    onSuccess: () => {
      toast.success('Contraseña cambiada. Las otras sesiones se cerraron.');
      cerrar();
    },
    onError: (e) => setError(e instanceof ApiError ? e.message : 'No se pudo cambiar la contraseña'),
  });

  function submit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    if (!actual) return setError('Escriba su contraseña actual');
    if (nueva.length < 8) return setError('La contraseña nueva debe tener al menos 8 caracteres');
    if (nueva !== confirmar) return setError('Las contraseñas nuevas no coinciden');
    if (nueva === actual) return setError('La contraseña nueva debe ser distinta de la actual');
    cambio.mutate();
  }

  return (
    <Modal open={open} onClose={cerrar} title="Cambiar contraseña" size="sm">
      <form onSubmit={submit} className="space-y-4" noValidate>
        <Input
          label="Contraseña actual"
          type="password"
          autoComplete="current-password"
          value={actual}
          onChange={(e) => setActual(e.target.value)}
        />
        <Input
          label="Contraseña nueva (mínimo 8 caracteres)"
          type="password"
          autoComplete="new-password"
          value={nueva}
          onChange={(e) => setNueva(e.target.value)}
        />
        <Input
          label="Repita la contraseña nueva"
          type="password"
          autoComplete="new-password"
          value={confirmar}
          onChange={(e) => setConfirmar(e.target.value)}
        />
        {error && (
          <div className="flex items-center gap-2 rounded-md bg-red-50 px-3 py-2 text-sm text-red-600 dark:bg-red-900/20 dark:text-red-300">
            <AlertCircle className="h-4 w-4 shrink-0" /> {error}
          </div>
        )}
        <div className="flex justify-end gap-2">
          <Button type="button" variant="ghost" onClick={cerrar}>
            Cancelar
          </Button>
          <Button type="submit" loading={cambio.isPending}>
            Guardar contraseña
          </Button>
        </div>
      </form>
    </Modal>
  );
}
